import React, { useContext, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControl, Grid, MenuItem, Select, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import AppContext from '../../../Services/AppContext';
import PersistantData from '../../../Services/PersistantData';
import API_Service, { axiosDefaultConfig } from '../../../Services/API_Service';
import { req_gallery_rotateimage } from '../../../Helper/Constants';
import PhotoViewer from '../../../Components/PhotoViewer';
import StatusConfirmDialog from './StatusConfirmDialog';

const STATUS_KEYS = ['pending', 'approved', 'rejected'];
const STATUS_CONFIG = {
    pending:   { color: '#ffb74d', label: 'pending' },
    approved:  { color: '#66bb6a', label: 'approved' },
    rejected:  { color: '#ef5350', label: 'rejected' },
    cancelled: { color: '#9e9e9e', label: 'cancelled' }
};

const useStyles = makeStyles((theme) => ({
    section: {
        marginBottom: theme.spacing(2)
    },
    label: {
        fontSize: 11,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        marginBottom: theme.spacing(0.5)
    },
    value: {
        fontSize: 14
    },
    reasonBox: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.action.hover,
        borderRadius: 6,
        whiteSpace: 'pre-wrap'
    },
    statusDropdown: {
        minWidth: 160
    },
    statusDot: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%'
    },
    statusMenuDot: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        marginRight: 6
    },
    attachmentRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1)
    },
    thumb: {
        width: 80,
        height: 80,
        objectFit: 'cover',
        borderRadius: 6,
        cursor: 'pointer',
        border: `1px solid ${theme.palette.divider}`
    },
    fileLink: {
        padding: theme.spacing(0.5, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        fontSize: 12,
        color: theme.palette.primary.main,
        textDecoration: 'none'
    },
    pdfTile: {
        width: 80,
        height: 80,
        borderRadius: 6,
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        color: theme.palette.text.primary,
        padding: theme.spacing(0.5)
    },
    pdfIcon: {
        color: '#d32f2f',
        fontSize: 32
    },
    pdfLabel: {
        fontSize: 10,
        marginTop: 2,
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%'
    }
}));

const isImage = (url) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(url || '');
const isPdf = (url) => /\.pdf(\?|$)/i.test(url || '');

export default function LeaveDetailDialog({ open, onClose, leaveList, selectedIndex, onStatusUpdated }) {
    const classes = useStyles();
    const singleton = PersistantData.getInstance();
    const [gState] = useContext(AppContext);
    var { translation: language, client } = gState;
    if (typeof (language) === 'undefined') {
        language = singleton.getlang();
    }

    const [imageView, setImageView] = useState({ open: false, index: 0 });
    const [confirmStatus, setConfirmStatus] = useState('');
    // upload_id -> url of the rotated image, so the dialog shows the saved rotation without reloading the list
    const [savedUrls, setSavedUrls] = useState({});
    const { enqueueSnackbar } = useSnackbar();

    const formatStatus = (raw) => {
        const t = (language && language[raw]) || raw;
        if (/^[A-Z]+$/.test(t)) {
            return t.charAt(0) + t.slice(1).toLowerCase();
        }
        return t;
    };

    const row = leaveList && leaveList.length > selectedIndex ? leaveList[selectedIndex] : null;

    if (!row) return null;

    const attachmentIds = Array.isArray(row.leave_attachment_ids) ? row.leave_attachment_ids : [];
    const attachments = (Array.isArray(row.leave_attachment) ? row.leave_attachment : [])
        .map((url, idx) => savedUrls[attachmentIds[idx]] || url);
    const imageUrls = attachments.filter(isImage);
    const currentStatus = row.status || 'pending';
    const isLocked = currentStatus === 'cancelled';
    const menuKeys = isLocked ? ['cancelled'] : STATUS_KEYS;

    const handleSaveRotation = (src, angle) => {
        const uploadId = attachmentIds[attachments.indexOf(src)];
        if (!uploadId) {
            enqueueSnackbar(language.update_fail, { variant: 'error' });
            return Promise.resolve(false);
        }
        const formdata = new FormData();
        formdata.append('upload_id', uploadId);
        formdata.append('angle', angle);
        return API_Service.post(req_gallery_rotateimage, formdata, axiosDefaultConfig(client)).then(({ data }) => {
            if (parseInt(data.result) === 1 && data.extraData && data.extraData.upload_image) {
                setSavedUrls(prev => ({ ...prev, [uploadId]: data.extraData.upload_image }));
                enqueueSnackbar(language.saved, { variant: 'success' });
                return true;
            }
            enqueueSnackbar(data.message || language.update_fail, { variant: 'error' });
            return false;
        }).catch(() => {
            enqueueSnackbar(language.network_error, { variant: 'error' });
            return false;
        });
    };

    const fmt = (d, withTime) => {
        if (!d) return '-';
        const m = moment(d, 'YYYY-MM-DD HH:mm:ss');
        return withTime ? m.format('DD/MM/YYYY HH:mm') : m.format('DD/MM/YYYY');
    };

    const sessionLabel = (s) => (s === 'pm' ? language.session_pm : language.session_am);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {language.leaves} - {row.agent_name || row.salesperson_id}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} className={classes.section}>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.apply_date}</Typography>
                            <Typography className={classes.value}>{fmt(row.created_at)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.agent}</Typography>
                            <Typography className={classes.value}>{row.agent_name || '-'}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.type}</Typography>
                            <Typography className={classes.value}>{row.leave_type_name || row.lt_code}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.leave_from}</Typography>
                            <Typography className={classes.value}>{fmt(row.leave_from)}{row.start_session ? ` (${sessionLabel(row.start_session)})` : ''}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.leave_to}</Typography>
                            <Typography className={classes.value}>{fmt(row.leave_to)}{row.end_session ? ` (${sessionLabel(row.end_session)})` : ''}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.leave_days}</Typography>
                            <Typography className={classes.value}>{row.leave_days}</Typography>
                        </Grid>
                    </Grid>

                    <Divider />

                    <div className={classes.section} style={{ marginTop: 16 }}>
                        <Typography className={classes.label}>{language.leave_reason}</Typography>
                        <div className={classes.reasonBox}>
                            <Typography className={classes.value}>{row.leave_reason || '-'}</Typography>
                        </div>
                    </div>

                    {attachments.length > 0 && (
                        <div className={classes.section}>
                            <Typography className={classes.label}>{language.attachments}</Typography>
                            <div className={classes.attachmentRow}>
                                {attachments.map((url, idx) => {
                                    if (isImage(url)) {
                                        return (
                                            <img
                                                key={idx}
                                                src={url}
                                                alt={`attachment-${idx}`}
                                                className={classes.thumb}
                                                onClick={() => setImageView({ open: true, index: imageUrls.indexOf(url) })}
                                            />
                                        );
                                    }
                                    if (isPdf(url)) {
                                        return (
                                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className={classes.pdfTile}>
                                                <PictureAsPdfIcon className={classes.pdfIcon} />
                                                <span className={classes.pdfLabel}>{`PDF ${idx + 1}`}</span>
                                            </a>
                                        );
                                    }
                                    return (
                                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className={classes.fileLink}>
                                            {`${language.attachments} ${idx + 1}`}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <Divider />

                    <Grid container spacing={2} className={classes.section} style={{ marginTop: 16 }}>
                        <Grid item xs={12} sm={4}>
                            <Typography className={classes.label}>{language.status}</Typography>
                            <FormControl size="small" variant="outlined">
                                <Select
                                    value={currentStatus}
                                    disabled={isLocked}
                                    className={classes.statusDropdown}
                                    renderValue={(val) => {
                                        const cfg = STATUS_CONFIG[val];
                                        if (!cfg) return val;
                                        return (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span className={classes.statusDot} style={{ backgroundColor: cfg.color }} />
                                                {formatStatus(val)}
                                            </span>
                                        );
                                    }}
                                    onChange={(e) => {
                                        const newKey = e.target.value;
                                        if (newKey !== currentStatus) {
                                            setConfirmStatus(newKey);
                                        }
                                    }}
                                >
                                    {menuKeys.map((statusKey) => {
                                        const cfg = STATUS_CONFIG[statusKey];
                                        const disableItem = (currentStatus === 'approved' || currentStatus === 'rejected') && statusKey === 'pending';
                                        return (
                                            <MenuItem key={statusKey} value={statusKey} disabled={disableItem} style={{ color: cfg.color, fontSize: 13 }}>
                                                <span className={classes.statusMenuDot} style={{ backgroundColor: cfg.color }} />
                                                {formatStatus(statusKey)}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.approved_by}</Typography>
                            <Typography className={classes.value}>{row.approved_by || '-'}</Typography>
                            <Typography className={classes.label} style={{ marginTop: 8 }}>{language.approved_date}</Typography>
                            <Typography className={classes.value}>{fmt(row.approved_at)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography className={classes.label}>{language.rejected_by}</Typography>
                            <Typography className={classes.value}>{row.rejected_by || '-'}</Typography>
                            <Typography className={classes.label} style={{ marginTop: 8 }}>{language.rejected_date}</Typography>
                            <Typography className={classes.value}>{fmt(row.rejected_at)}</Typography>
                        </Grid>
                    </Grid>

                    {row.status === 'rejected' && row.reject_reason && (
                        <div className={classes.section}>
                            <Typography className={classes.label}>{language.reject_reason}</Typography>
                            <div className={classes.reasonBox}>
                                <Typography className={classes.value}>{row.reject_reason}</Typography>
                            </div>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{language.cancel}</Button>
                </DialogActions>
            </Dialog>

            <PhotoViewer
                open={imageView.open}
                images={imageUrls}
                index={imageView.index}
                onClose={() => setImageView(prev => ({ ...prev, open: false }))}
                onSaveRotation={handleSaveRotation}
            />

            <StatusConfirmDialog
                open={!!confirmStatus}
                row={row}
                newStatus={confirmStatus}
                onClose={() => setConfirmStatus('')}
                onSuccess={() => {
                    setConfirmStatus('');
                    if (onStatusUpdated) onStatusUpdated();
                    onClose();
                }}
            />
        </>
    );
}
